import { useEffect, useMemo, useState } from 'react';
import { searchCertifications } from '../../services/content/certificationService';
import { createClientCertification } from '../../services/clientCertificationService';
import type { ContentCertification } from '../../types/content/certification';

interface Props {
  clientId: string;
  /** Certification IDs already assigned to this client — filtered out of the picker. */
  existingCertificationIds: string[];
  onClose: () => void;
  onSuccess: () => void;
}

export function AddClientCertificationModal({ clientId, existingCertificationIds, onClose, onSuccess }: Props) {
  const [certifications, setCertifications] = useState<ContentCertification[]>([]);
  const [certificationId, setCertificationId] = useState('');
  const [order, setOrder] = useState<string>('0');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    searchCertifications({}, { pageSize: 500, orderBy: 'name', order: 'asc' })
      .then(setCertifications)
      .catch(() => {});
  }, []);

  const existingSet = useMemo(() => new Set(existingCertificationIds), [existingCertificationIds]);
  const available = useMemo(
    () => certifications.filter((c) => !existingSet.has(c.id)),
    [certifications, existingSet]
  );

  // When a certification is picked, default the order input to that
  // certification's DefaultOrder — the admin can still override.
  function pickCertification(id: string) {
    setCertificationId(id);
    const cert = certifications.find((c) => c.id === id);
    if (cert && cert.defaultOrder != null) {
      setOrder(String(cert.defaultOrder));
    }
  }

  async function handleSave() {
    if (!certificationId) return;
    setError('');
    setLoading(true);
    try {
      await createClientCertification({
        clientId,
        certificationId,
        order: order.trim() ? parseInt(order, 10) : 0,
      });
      onSuccess();
    } catch {
      setError('Failed to add certification');
    } finally {
      setLoading(false);
    }
  }

  const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm';
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1';
  const btnOutline = 'px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors';
  const btnPrimary = 'px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-lg w-full max-w-md mx-4 max-h-[90vh] flex flex-col">
        <div className="px-6 pt-6 pb-4 overflow-y-auto flex-1">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Add Certification</h2>

          <div className="space-y-4">
            <div>
              <label className={labelClass}>Certification *</label>
              <select
                value={certificationId}
                onChange={(e) => pickCertification(e.target.value)}
                className={inputClass}
              >
                <option value="">
                  {available.length === 0
                    ? (certifications.length === 0 ? 'Loading…' : 'All certifications already assigned')
                    : 'Select…'}
                </option>
                {available.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>Order</label>
              <input
                type="number"
                value={order}
                onChange={(e) => setOrder(e.target.value)}
                className={inputClass}
                placeholder="0"
              />
              <p className="text-xs text-gray-500 mt-1">Defaults to the certification's DefaultOrder; override for this client's sequence.</p>
            </div>
          </div>

          {error && <p className="text-red-600 text-sm mt-3">{error}</p>}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
          <button type="button" onClick={onClose} className={btnOutline}>Cancel</button>
          <button
            type="button"
            onClick={handleSave}
            disabled={loading || !certificationId}
            className={btnPrimary}
          >
            {loading ? 'Adding…' : 'Add'}
          </button>
        </div>
      </div>
    </div>
  );
}
